/**
 * Sketch persistence — brief §18.
 *
 * All persistence goes through SketchRepository. Web: localStorage with
 * explicit quota handling (a save NEVER throws on quota and NEVER loses
 * in-memory work). Native: Capacitor Filesystem (Phase 9).
 */
import type { SketchDocument, SketchLayerData, SketchMeta } from '@/types/drawing';
import { Capacitor } from '@capacitor/core';
import { Directory, Encoding, Filesystem } from '@capacitor/filesystem';

export type SaveResult = { ok: true } | { ok: false; reason: 'quota'; bytesNeeded?: number };

/** Migrate legacy (single-strokes) documents to the multi-layer shape. */
export function normalizeSketchDocument(doc: SketchDocument): SketchDocument {
  if (!doc.sketch.layers) {
    const legacy = (doc.sketch as { strokes?: SketchLayerData['strokes'] }).strokes ?? [];
    const layer: SketchLayerData = {
      id: `migrated-${doc.meta.id}`,
      name: 'Layer 1',
      strokes: legacy,
      opacity: 1,
      visible: true,
      png: null,
    };
    doc.sketch = { layers: [layer] };
  }
  return doc;
}

export interface SketchRepository {
  list(): Promise<SketchMeta[]>;
  load(id: string): Promise<SketchDocument>;
  /** Never throws on quota — returns { ok: false, reason: 'quota' }. */
  save(doc: SketchDocument): Promise<SaveResult>;
  remove(id: string): Promise<void>;
  /** Approximate bytes used by saved sketches (for the storage dialog). */
  usage(): Promise<{ id: string; bytes: number }[]>;
}

const INDEX_KEY = 'esch_index';
const SKETCH_PREFIX = 'esch_sketch_';

function isQuotaError(e: unknown): boolean {
  return (
    e instanceof DOMException &&
    (e.name === 'QuotaExceededError' || e.name === 'NS_ERROR_DOM_QUOTA_REACHED' || e.code === 22)
  );
}

/** Minimal localStorage surface, injectable for tests. */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export class LocalStorageSketchRepository implements SketchRepository {
  private storage: KeyValueStore;

  constructor(storage?: KeyValueStore) {
    this.storage = storage ?? globalThis.localStorage;
  }

  private readIndex(): SketchMeta[] {
    const raw = this.storage.getItem(INDEX_KEY);
    if (!raw) return [];
    try {
      return JSON.parse(raw) as SketchMeta[];
    } catch {
      return [];
    }
  }

  private writeIndex(metas: SketchMeta[]): void {
    this.storage.setItem(INDEX_KEY, JSON.stringify(metas));
  }

  async list(): Promise<SketchMeta[]> {
    return this.readIndex().sort((a, b) => b.updatedAt - a.updatedAt);
  }

  async load(id: string): Promise<SketchDocument> {
    const raw = this.storage.getItem(SKETCH_PREFIX + id);
    if (!raw) throw new Error(`Sketch not found: ${id}`);
    return normalizeSketchDocument(JSON.parse(raw) as SketchDocument);
  }

  async save(doc: SketchDocument): Promise<SaveResult> {
    const payload = JSON.stringify(doc);
    try {
      this.storage.setItem(SKETCH_PREFIX + doc.meta.id, payload);
    } catch (e) {
      if (!isQuotaError(e)) throw e;
      // Storage pressure: drop the thumbnail and retry once (§18 rule 5).
      if (doc.meta.thumbnail !== null) {
        const slim: SketchDocument = { ...doc, meta: { ...doc.meta, thumbnail: null } };
        try {
          this.storage.setItem(SKETCH_PREFIX + doc.meta.id, JSON.stringify(slim));
          this.updateIndexEntry(slim.meta);
          return { ok: true };
        } catch (e2) {
          if (!isQuotaError(e2)) throw e2;
        }
      }
      return { ok: false, reason: 'quota', bytesNeeded: payload.length * 2 };
    }
    this.updateIndexEntry(doc.meta);
    return { ok: true };
  }

  private updateIndexEntry(meta: SketchMeta): void {
    const metas = this.readIndex().filter((m) => m.id !== meta.id);
    metas.push(meta);
    try {
      this.writeIndex(metas);
    } catch (e) {
      if (!isQuotaError(e)) throw e;
      // Index write under quota pressure: strip all thumbnails from the index.
      const slim = metas.map((m) => ({ ...m, thumbnail: null }));
      this.writeIndex(slim);
    }
  }

  async remove(id: string): Promise<void> {
    this.storage.removeItem(SKETCH_PREFIX + id);
    this.writeIndex(this.readIndex().filter((m) => m.id !== id));
  }

  async usage(): Promise<{ id: string; bytes: number }[]> {
    return this.readIndex().map((m) => {
      const raw = this.storage.getItem(SKETCH_PREFIX + m.id);
      // UTF-16: ~2 bytes per code unit
      return { id: m.id, bytes: (raw?.length ?? 0) * 2 };
    });
  }
}

/**
 * Native repository — real app-sandbox disk via Capacitor Filesystem,
 * bounded by actual free device storage rather than a WebView quota.
 *
 * Layout under Directory.Documents:
 *   sketches/index.json          — SketchMeta[]
 *   sketches/<id>.json           — full SketchDocument
 */
const SKETCH_DIR = 'sketches';
const INDEX_PATH = `${SKETCH_DIR}/index.json`;

function sketchPath(id: string): string {
  return `${SKETCH_DIR}/${id}.json`;
}

async function isNotFound(e: unknown): Promise<boolean> {
  // Capacitor Filesystem throws a plain Error with a message on missing files
  // rather than a typed error class — match on message text.
  return e instanceof Error && /does not exist|not found|ENOENT/i.test(e.message);
}

export class FilesystemSketchRepository implements SketchRepository {
  private dirReady: Promise<void> | null = null;

  private async ensureDir(): Promise<void> {
    if (!this.dirReady) {
      this.dirReady = Filesystem.mkdir({
        path: SKETCH_DIR,
        directory: Directory.Documents,
        recursive: true,
      })
        .catch(async (e) => {
          // Already exists — fine. Anything else, surface it.
          if (!(e instanceof Error && /exist/i.test(e.message))) throw e;
        })
        .then(() => undefined);
    }
    return this.dirReady;
  }

  private async readIndex(): Promise<SketchMeta[]> {
    await this.ensureDir();
    try {
      const { data } = await Filesystem.readFile({
        path: INDEX_PATH,
        directory: Directory.Documents,
        encoding: Encoding.UTF8,
      });
      return JSON.parse(data as string) as SketchMeta[];
    } catch (e) {
      if (await isNotFound(e)) return [];
      throw e;
    }
  }

  private async writeIndex(metas: SketchMeta[]): Promise<void> {
    await this.ensureDir();
    await Filesystem.writeFile({
      path: INDEX_PATH,
      directory: Directory.Documents,
      encoding: Encoding.UTF8,
      data: JSON.stringify(metas),
    });
  }

  async list(): Promise<SketchMeta[]> {
    return (await this.readIndex()).sort((a, b) => b.updatedAt - a.updatedAt);
  }

  async load(id: string): Promise<SketchDocument> {
    const { data } = await Filesystem.readFile({
      path: sketchPath(id),
      directory: Directory.Documents,
      encoding: Encoding.UTF8,
    });
    return normalizeSketchDocument(JSON.parse(data as string) as SketchDocument);
  }

  async save(doc: SketchDocument): Promise<SaveResult> {
    await this.ensureDir();
    try {
      await Filesystem.writeFile({
        path: sketchPath(doc.meta.id),
        directory: Directory.Documents,
        encoding: Encoding.UTF8,
        data: JSON.stringify(doc),
      });
    } catch {
      // Real disk-full is the closest native analogue to a web quota error —
      // same contract, never throw, in-memory work stays intact.
      return { ok: false, reason: 'quota' };
    }
    await this.updateIndexEntry(doc.meta);
    return { ok: true };
  }

  private async updateIndexEntry(meta: SketchMeta): Promise<void> {
    const metas = (await this.readIndex()).filter((m) => m.id !== meta.id);
    metas.push(meta);
    await this.writeIndex(metas);
  }

  async remove(id: string): Promise<void> {
    try {
      await Filesystem.deleteFile({ path: sketchPath(id), directory: Directory.Documents });
    } catch (e) {
      if (!(await isNotFound(e))) throw e;
    }
    await this.writeIndex((await this.readIndex()).filter((m) => m.id !== id));
  }

  async usage(): Promise<{ id: string; bytes: number }[]> {
    const metas = await this.readIndex();
    const results: { id: string; bytes: number }[] = [];
    for (const m of metas) {
      try {
        const stat = await Filesystem.stat({ path: sketchPath(m.id), directory: Directory.Documents });
        results.push({ id: m.id, bytes: stat.size });
      } catch {
        results.push({ id: m.id, bytes: 0 });
      }
    }
    return results;
  }

  /** Writes the document file only, no index update — used by migration to
   *  batch all index changes into one write instead of N racy ones. */
  async writeDocOnly(doc: SketchDocument): Promise<void> {
    await this.ensureDir();
    await Filesystem.writeFile({
      path: sketchPath(doc.meta.id),
      directory: Directory.Documents,
      encoding: Encoding.UTF8,
      data: JSON.stringify(doc),
    });
  }

  /** Direct index overwrite — exposed for migration's single batched write. */
  async writeIndexPublic(metas: SketchMeta[]): Promise<void> {
    await this.writeIndex(metas);
  }

  /**
   * Recovery utility: rebuild index.json from whatever <id>.json files are
   * actually present on disk, reading each doc's own meta. Use this if the
   * index and the real files on disk ever diverge (e.g. from a lost race).
   * Physical sketch files are never touched — this only fixes the manifest.
   */
  async rebuildIndexFromDisk(): Promise<{ recovered: number }> {
    await this.ensureDir();
    const dir = await Filesystem.readdir({ path: SKETCH_DIR, directory: Directory.Documents });
    const ids = dir.files
      .map((f) => f.name)
      .filter((name) => name.endsWith('.json') && name !== 'index.json')
      .map((name) => name.replace(/\.json$/, ''));

    const metas: SketchMeta[] = [];
    for (const id of ids) {
      try {
        const doc = await this.load(id);
        metas.push(doc.meta);
      } catch (e) {
        console.error(`Could not read sketch file for recovery: ${id}`, e);
      }
    }
    await this.writeIndex(metas);
    return { recovered: metas.length };
  }
}

/**
 * One-time migration: if legacy sketches exist in localStorage (from before
 * Phase 9 wiring — or from this exact bug), copy them into the Filesystem
 * repository, then leave the localStorage copies in place untouched as a
 * safety net rather than deleting anything automatically.
 */
const MIGRATION_FLAG_KEY = 'esch_fs_migration_done';

async function migrateLegacyLocalStorageIfNeeded(target: FilesystemSketchRepository): Promise<void> {
  try {
    if (globalThis.localStorage.getItem(MIGRATION_FLAG_KEY)) return;
  } catch {
    return; // no localStorage available — nothing to migrate
  }

  const legacy = new LocalStorageSketchRepository();
  const legacyMetas = await legacy.list();

  if (legacyMetas.length > 0) {
    const existing = await target.list();
    const alreadyMigrated = new Set(existing.map((m) => m.id));
    const migratedMetas: SketchMeta[] = [];

    for (const meta of legacyMetas) {
      if (alreadyMigrated.has(meta.id)) continue;
      try {
        const doc = await legacy.load(meta.id);
        // Write the document file directly — bypass save()'s own index
        // read-modify-write here, since we're batching one index write
        // at the end instead of N concurrent ones.
        await target.writeDocOnly(doc);
        migratedMetas.push(doc.meta);
      } catch (e) {
        // Best-effort: skip a corrupt legacy entry rather than blocking the rest.
        console.error(`Migration failed for sketch ${meta.id}`, e);
      }
    }

    if (migratedMetas.length > 0) {
      // Single merged index write — re-read current state right before
      // writing to minimize (not eliminate) the remaining race window.
      const latest = await target.list();
      const merged = [...latest.filter((m) => !migratedMetas.some((mm) => mm.id === m.id)), ...migratedMetas];
      await target.writeIndexPublic(merged);
    }
  }

  try {
    globalThis.localStorage.setItem(MIGRATION_FLAG_KEY, '1');
  } catch {
    // ignore — worst case migration re-runs next launch, which is idempotent
  }
}

let repository: SketchRepository | null = null;
let migrationDone: Promise<void> | null = null;

export function getSketchRepository(): SketchRepository {
  if (!repository) {
    if (Capacitor.isNativePlatform()) {
      const fsRepo = new FilesystemSketchRepository();
      repository = fsRepo;
      // Fire-and-forget on first access; save()/load() calls that race the
      // migration will simply see a still-empty index until it completes,
      // same as any other cold-start race — acceptable for a one-time cost.
      migrationDone = migrateLegacyLocalStorageIfNeeded(fsRepo).catch((e) => {
        console.error('Legacy sketch migration failed', e);
      });
    } else {
      repository = new LocalStorageSketchRepository();
    }
  }
  return repository;
}

/** Await this once at app boot (e.g. before the sketchbook gallery renders)
 *  to avoid the empty-index race on first native launch after this fix ships. */
export async function ensureMigrationComplete(): Promise<void> {
  await migrationDone;
}

/** Test seam + native bootstrap. */
export function setSketchRepository(repo: SketchRepository | null): void {
  repository = repo;
}